import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { CircularProgress } from "@mui/material";
import { useState } from "react";
import ShowToast from "../../../components/alerts/all_toasts";
import { Link, useNavigate } from "react-router-dom";
import { RouteConstants } from "../../../helpers/RouteConstants";
import Button from "../../../components/Button";
import BaseService from "../../../helpers/baseServices";
import { persistAuthV2Session } from "../../../helpers/authSession";
import OtpVerifyStep from "../../../components/auth/OtpVerifyStep";

interface stepProps {
  dataFromStep1: any;
  goBack: () => void;
}

const SecondStep = ({ dataFromStep1, goBack }: stepProps) => {
  const navigate = useNavigate();
  const [isError, setIsError] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [wrongPasswords, setWrongPasswords] = useState(false);

  // Only set if registration comes back PASSWORD_SETUP_REQUIRED instead of
  // issuing a session directly — a fallback, not the normal path.
  const [needsVerify, setNeedsVerify] = useState(false);

  const handleCreateAccount = async () => {
    if (!password || !confirmPassword) {
      setIsError(true);
      return;
    }
    if (password !== confirmPassword) {
      setWrongPasswords(true);
      return;
    }
    if (password.length < 8) {
      ShowToast.error_toast("Password must be at least 8 characters");
      return;
    }
    setIsError(false);
    setWrongPasswords(false);
    setIsBusy(true);
    try {
      const response = await BaseService.post_api(
        RouteConstants.SIGN_UP_ROUTE,
        {
          first_name: dataFromStep1?.first_name,
          last_name: dataFromStep1?.last_name,
          phone: dataFromStep1?.phone,
          password,
        },
      );
      const payload = response?.data?.payload;
      if (payload?.status === "OK") {
        ShowToast.success_toast("Account created");
        persistAuthV2Session(payload);
        navigate("/home", { replace: true });
      } else {
        // Rare fallback — e.g. the phone already has an un-set-up
        // candidate sharing the SIM. Finish via the OTP-verify path.
        setNeedsVerify(true);
      }
    } catch (error: any) {
      ShowToast.error_toast(error?.response?.data?.message);
    } finally {
      setIsBusy(false);
    }
  };

  if (needsVerify) {
    return (
      <OtpVerifyStep
        identifier={dataFromStep1?.phone}
        purpose="LOGIN"
        title="Verify number"
        subtitle={`Enter the code sent to ${dataFromStep1?.phone} and choose a password.`}
        submitLabel="Create account"
        onBack={() => setNeedsVerify(false)}
        onSuccess={(payload) => {
          ShowToast.success_toast("Account created");
          persistAuthV2Session(payload);
          navigate("/home", { replace: true });
        }}
      />
    );
  }

  return (
    <div>
      <button onClick={() => goBack()}>
        <ChevronLeftIcon className="w-6 h-6" />
      </button>
      <div className="w-full mt-5">
        <div className="header-text-3">Create password</div>
        <div className="body-text-2 pt-1">
          Choose a password to secure your account.
        </div>
      </div>

      <div className="flex flex-col mt-8 relative">
        <small className={`${isError ? "text-red-500" : ""} mb-2`}>
          Your password
        </small>
        <input
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
        />
      </div>

      <div className="flex flex-col mt-8 relative">
        <small className={`${isError ? "text-red-500" : ""} mb-2`}>
          Confirm password
        </small>
        <input
          className="input"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your password"
        />
      </div>
      {wrongPasswords && (
        <div className="text-red-500 mt-2">Passwords don't match!</div>
      )}

      <div className="mt-[30px] flex justify-center items-center">
        <Button
          className="h-[55px] bg-primary text-white rounded-[8px] w-full flex justify-center items-center disabled:cursor-not-allowed disabled:bg-gray-400"
          disabled={isBusy}
          onClick={() => handleCreateAccount()}
        >
          {isBusy ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : (
            <span>Create account</span>
          )}
        </Button>
      </div>
      <div className="mt-5 flex items-center gap-2">
        <p>Already have an account?</p>
        <Link className="btn-subtle" to="/signin">
          Sign In
        </Link>
      </div>
    </div>
  );
};

export default SecondStep;
